import { QualityStatus, useGetWaterQualityOverTime, SensorType } from 'common';
import React from 'react';
import { XStack, YStack, Text, View } from 'tamagui';
import { tokens } from 'tamagui-config/src/tokens';

import { useAppSync } from '../../../contexts/AppSyncContext';

const ALL_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIMES = ['4:00 AM', '8:00 AM', '12:00 PM', '4:00 PM', '8:00 PM', '12:00 AM'];

const getQualityColor = (quality: QualityStatus): string => {
  switch (quality) {
    case 'good':
      return tokens.color.successGreen;
    case 'moderate':
      return tokens.color.warning;
    case 'poor':
      return tokens.color.energyRed;
    case 'none':
    default:
      return tokens.color.gray8;
  }
};

export const WaterQualityOverTimeContent: React.FC = () => {
  const { sensors, selectedHubId } = useAppSync();
  const sensor = sensors.find(
    (s) => s.hubId === selectedHubId && s.type === SensorType.WATER_QUALITY,
  );
  const { data, error } = useGetWaterQualityOverTime(sensor?.id);

  const DAYS = React.useMemo(() => {
    const currentDay = new Date().getDay();
    const days: string[] = [];
    for (let i = 0; i < 7; i++) {
      const dayIndex = (currentDay - i + 7) % 7;
      days.unshift(ALL_DAYS[dayIndex]);
    }
    return days;
  }, []);

  const getQualityForTimeSlot = (dayIndex: number, timeIndex: number): QualityStatus => {
    const dataPoint = data.find((d) => d.dayIndex === dayIndex && d.timeIndex === timeIndex);
    return dataPoint?.quality || 'none';
  };
  const containerWidth = 600;
  const timeLabelWidth = containerWidth * 0.3;
  const availableWidthForCells = containerWidth - timeLabelWidth;
  const cellWidth = availableWidthForCells / DAYS.length;
  const cellHeight = cellWidth / 1.55;

  if (error || !data.length) {
    return (
      <YStack height={140} justifyContent="center" alignItems="center">
        <Text color={tokens.color.white12}>{error || 'No water quality data available'}</Text>
      </YStack>
    );
  }

  return (
    <YStack gap={8}>
      <XStack justifyContent="space-between" alignItems="center" gap={8} marginBottom={'$1'}>
        {DAYS.map((day) => (
          <Text
            key={day}
            fontSize="$3"
            fontWeight="600"
            color="$textLight"
            textAlign="center"
            width={cellWidth}>
            {day}
          </Text>
        ))}
        <View width={timeLabelWidth * 0.4} />
      </XStack>

      {TIMES.map((time, timeIndex) => (
        <XStack key={time} justifyContent="space-between" alignItems="center" gap={8}>
          {DAYS.map((_, dayIndex) => {
            const quality = getQualityForTimeSlot(dayIndex, timeIndex);
            return (
              <View
                key={`${dayIndex}-${timeIndex}`}
                backgroundColor={getQualityColor(quality)}
                width={cellWidth}
                height={cellHeight}
                borderRadius={'$2'}
              />
            );
          })}
          <Text
            fontSize="$3"
            color="$textLight"
            fontWeight="600"
            width={timeLabelWidth}
            textAlign="left">
            {time}
          </Text>
        </XStack>
      ))}
    </YStack>
  );
};
